/*
 * Lookbook feed: tag chips, HIDE PRODUCTS, the SHOP THE LOOK drawer, the story
 * landing and the set rule.
 *
 * The drawer is one shared element per feed; the body of every look is rendered
 * by Liquid inside a template element and cloned in on open, so nothing is
 * fetched. It extends the base theme's MenuDrawer for focus trapping, Escape
 * and click-outside, with its own body classes.
 *
 * HIDE PRODUCTS is one state for the whole page: every card shows or hides its
 * panel together, remembered for the session. Any card's toggle flips it.
 *
 * Story landing (?look=HANDLE): that look moves to the top as the featured
 * card; with the "split" layout its items are cloned from the same template
 * next to the photo on wide screens (radio names get a suffix so the inline
 * copy and the drawer never share a group). Below it, MORE LOOKS shows the
 * next N cards as a row and an ALL LOOKS link leads back to the feed; the tag
 * chips are hidden there. A ?tag= link opens the feed with that chip pressed.
 *
 * Every look is in the page; cards past the first batch carry
 * data-lookbook-later and LOAD MORE reveals the next batch. A tag filter
 * (lookbook-feed--filtered) and the story landing reach every card. A look
 * whose every item is sold out is in the page too, hidden and marked
 * data-lookbook-archived: only its own story link opens it; the filter, the
 * counts and MORE LOOKS leave it out.
 *
 * The tag chips stick under the sticky header while the feed scrolls.
 *
 * Attribution: an add that starts from a look (set or single item) writes the
 * look handle to the cart attribute "lookbook" on the click, alongside the base
 * theme's own add request, so the order carries where the sale came from.
 *
 * Set rule (kept by reconcileRow after every size or color change): the hidden
 * input[name="id"] that the base theme's bundle-products element collects exists
 * only for a resolved item. A product with several available sizes is resolved
 * only by an explicit choice; exactly one available size resolves on its own;
 * sold-out products never join the set. The set button stays disabled and reads
 * the "pick sizes" label until every available item is resolved.
 */
(function () {
  var STORAGE_KEY = 'lookbook-feed:products-hidden';

  function resolveRow(input) {
    if (!input.available) return { state: 'soldout', variantId: '' };
    var sizes = input.sizes || [];
    if (sizes.length) {
      var checked = null;
      var enabled = [];
      for (var i = 0; i < sizes.length; i++) {
        if (!sizes[i].enabled) continue;
        enabled.push(sizes[i]);
        if (sizes[i].checked && !checked) checked = sizes[i];
      }
      if (checked) return { state: 'resolved', variantId: String(checked.id) };
      if (enabled.length === 1) return { state: 'resolved', variantId: String(enabled[0].id), autoSelected: true };
      return { state: 'unresolved', variantId: '' };
    }
    if (input.fallbackId) return { state: 'resolved', variantId: String(input.fallbackId) };
    return { state: 'unresolved', variantId: '' };
  }

  function summarizeSet(rows, labels, formatMoney) {
    var inSet = rows.filter(function (row) { return row.state !== 'soldout'; });
    if (!inSet.length) return { disabled: true, label: labels.soldOut, count: 0, total: 0 };
    var missing = inSet.filter(function (row) { return row.state !== 'resolved'; });
    if (missing.length) return { disabled: true, label: labels.pick, count: 0, total: 0 };
    var total = inSet.reduce(function (sum, row) { return sum + (Number(row.price) || 0); }, 0);
    return {
      disabled: false,
      count: inSet.length,
      total: total,
      label: labels.add + ' · ' + inSet.length + ' ' + labels.items + ' · ' + formatMoney(total)
    };
  }

  // A money attribute is a number or absent; zero is a price, not a gap.
  function parsePrice(value) {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    var number = Number(value);
    return isNaN(number) ? null : number;
  }

  // The price of what will actually be posted: the resolved variant's own price
  // when the pills carry one, else the current color's product price.
  function rowPrice(read, result) {
    if (result.state === 'resolved') {
      var sizes = read.sizes || [];
      for (var i = 0; i < sizes.length; i++) {
        var pillPrice = parsePrice(sizes[i].price);
        if (String(sizes[i].id) === result.variantId && pillPrice !== null) return pillPrice;
      }
    }
    var price = parsePrice(read.price);
    return price === null ? 0 : price;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { resolveRow: resolveRow, summarizeSet: summarizeSet, rowPrice: rowPrice, parsePrice: parsePrice };
  }
  if (typeof document === 'undefined' || typeof customElements === 'undefined') return;
  if (window.__lookbookFeedInit) return;
  window.__lookbookFeedInit = true;

  // Same output as the theme's price-money element: shop money format, plus the
  // ISO code when the "show currency code" setting is on.
  function money(cents) {
    var settings = (window.theme && theme.shopSettings) || {};
    var text = window.theme && theme.Currency && settings.moneyFormat
      ? theme.Currency.formatMoney(cents, settings.moneyFormat)
      : String(Math.round(cents / 100));
    return settings.currencyCode && settings.isoCode ? text + ' ' + settings.isoCode : text;
  }

  function readRow(row) {
    var currentSwatch = row.querySelector('[data-lb-swatch].lookbook-swatch--current');
    var available = currentSwatch
      ? currentSwatch.getAttribute('data-soldout') !== 'true'
      : row.getAttribute('data-available') === 'true';
    var price = parsePrice(row.getAttribute('data-price'));
    if (currentSwatch && parsePrice(currentSwatch.getAttribute('data-price')) !== null) price = parsePrice(currentSwatch.getAttribute('data-price'));
    var pills = row.querySelectorAll('.lookbook-size__input');
    var sizes = [];
    for (var i = 0; i < pills.length; i++) {
      sizes.push({ id: pills[i].value, enabled: !pills[i].disabled, checked: pills[i].checked, price: parsePrice(pills[i].getAttribute('data-price')) });
    }
    var add = row.querySelector('[data-lookbook-add]');
    var fallbackId = (add && add.getAttribute('data-variant-id')) || row.getAttribute('data-default-variant') || '';
    return { available: available, sizes: sizes, fallbackId: fallbackId, price: price, pills: pills, add: add };
  }

  function reconcileRow(row) {
    var read = readRow(row);
    var result = resolveRow(read);
    var hidden = row.querySelector('input[name="id"]');

    if (result.state === 'resolved') {
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'id';
        hidden.setAttribute('data-lookbook-id', '');
        row.querySelector('.lookbook-drawer__info').appendChild(hidden);
      }
      hidden.value = result.variantId;
      if (result.autoSelected) {
        for (var i = 0; i < read.pills.length; i++) {
          if (read.pills[i].value === result.variantId) read.pills[i].checked = true;
        }
      }
    } else if (hidden) {
      hidden.parentNode.removeChild(hidden);
    }

    if (read.add) {
      read.add.setAttribute('data-variant-id', result.state === 'resolved' ? result.variantId : '');
      if (result.state === 'resolved') read.add.removeAttribute('aria-disabled');
      else read.add.setAttribute('aria-disabled', 'true');
    }

    row.classList.toggle('lookbook-drawer__item--soldout', result.state === 'soldout');
    var note = row.querySelector('[data-lookbook-note]');
    if (note) note.hidden = result.state !== 'soldout';
    if (result.state !== 'unresolved') {
      var hint = row.querySelector('[data-lookbook-hint]');
      if (hint) hint.hidden = true;
    }
    var price = rowPrice(read, result);
    var priceEl = row.querySelector('[data-lookbook-price]');
    if (priceEl) priceEl.textContent = money(price);

    return { state: result.state, price: price };
  }

  function reconcileSet(set) {
    if (!set) return;
    var rows = set.querySelectorAll('bundle-product');
    var states = [];
    for (var i = 0; i < rows.length; i++) {
      var state = reconcileRow(rows[i]);
      states.push(state);
      // a row resolved by any path (size pick, color swap) drops the missing-size mark
      if (state.state !== 'unresolved') rows[i].classList.remove('lookbook-drawer__item--needs-size');
    }
    var button = set.querySelector('[data-lookbook-submit]');
    if (!button) return;
    var summary = summarizeSet(states, {
      add: set.getAttribute('data-label-add') || '',
      pick: set.getAttribute('data-label-pick') || '',
      items: set.getAttribute('data-label-items') || '',
      soldOut: set.getAttribute('data-label-soldout') || ''
    }, money);
    button.textContent = summary.label;
    // A fully sold-out look keeps a real disabled button. While sizes are still
    // missing the button stays focusable and tappable (aria-disabled) so a tap
    // can point at the item that still needs a size instead of doing nothing.
    var soldOut = !states.some(function (row) { return row.state !== 'soldout'; });
    button.disabled = soldOut;
    if (summary.disabled && !soldOut) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
    // announce the set state to screen readers when it changes
    var status = set.querySelector('[data-lookbook-set-status]');
    if (status && status.textContent !== summary.label) status.textContent = summary.label;
  }

  function showHint(row) {
    var hint = row.querySelector('[data-lookbook-hint]');
    if (hint) hint.hidden = false;
    var pill = row.querySelector('.lookbook-size__input:not(:disabled)');
    if (pill) pill.focus();
  }

  // Feedback for a tap on the set button while a size is missing: the button
  // names the item for a moment, and that item is brought into view and marked.
  function flagMissing(set, row, button) {
    var flagged = set.querySelectorAll('.lookbook-drawer__item--needs-size');
    for (var i = 0; i < flagged.length; i++) flagged[i].classList.remove('lookbook-drawer__item--needs-size');
    row.classList.add('lookbook-drawer__item--needs-size');
    var hint = row.querySelector('[data-lookbook-hint]');
    if (hint) hint.hidden = false;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (row.scrollIntoView) row.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    var title = row.querySelector('.lookbook-drawer__product');
    var pick = set.getAttribute('data-label-pick-one') || '';
    if (title && pick) {
      clearTimeout(button._lookbookFlagTimer);
      button.textContent = pick + ': ' + title.textContent.trim();
      button._lookbookFlagTimer = setTimeout(function () { reconcileSet(set); }, 2600);
    }
    var pill = row.querySelector('.lookbook-size__input:not(:disabled)');
    if (pill) pill.focus({ preventScroll: true });
  }

  function scrollToRow(set, productId) {
    if (!productId) return;
    var row = set.querySelector('[data-lookbook-product="' + productId + '"]');
    var rows = set.querySelector('.lookbook-drawer__rows');
    if (row && rows) rows.scrollTop = row.offsetTop - rows.offsetTop;
  }

  class LookbookDrawer extends MenuDrawer {
    constructor() {
      super();
      this.setClasses({
        open: 'lookbook-drawer--open',
        opening: 'lookbook-drawer--opening',
        closing: 'lookbook-drawer--closing'
      });
      this.drawerPanel = this.querySelector('[data-lookbook-drawer-panel]');
      this.drawerBody = this.querySelector('[data-lookbook-drawer-body]');
      this.drawerTitle = this.querySelector('[data-lookbook-drawer-title]');
      this.closeButton = this.querySelector('[data-lookbook-close]');
      this.trigger = null;

      this.closeButton.addEventListener('click', (event) => this.closeMenuDrawer(event, this.trigger));
      this.addEventListener('close', () => {
        this.drawerBody.innerHTML = '';
        this.trigger = null;
      });
    }

    isOpen() {
      return this.mainDetailsToggle.hasAttribute('open');
    }

    onKeyUp(event) {
      var key = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
      if (key !== 'ESCAPE' || !this.isOpen()) return;
      this.closeMenuDrawer(event, this.trigger);
    }

    // a click outside closes like the base drawer, but focus goes back to the opener
    onBodyClick(event) {
      if (!this.contains(event.target)) this.closeMenuDrawer(event, this.trigger);
    }

    open(handle, options) {
      var scope = this.closest('[data-lookbook-feed]') || document;
      var template = scope.querySelector('template[data-lookbook-overlay="' + CSS.escape(handle) + '"]');
      if (!template) return false;
      this.trigger = options.trigger || null;
      this.drawerTitle.textContent = options.name || '';
      this.drawerBody.innerHTML = '';
      this.drawerBody.appendChild(document.importNode(template.content, true));
      var set = this.drawerBody.querySelector('[data-lookbook-set]');
      reconcileSet(set);
      this.openMenuDrawer();
      setTimeout(() => {
        trapFocus(this.drawerPanel, this.closeButton);
        scrollToRow(this.drawerBody, options.productId);
      }, 120);
      return true;
    }
  }
  customElements.define('lookbook-drawer', LookbookDrawer);

  function feedFor(element) {
    return element.closest('[data-lookbook-feed]');
  }

  function readHidden() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (_) {
      return false;
    }
  }

  function writeHidden(hidden) {
    try {
      if (hidden) sessionStorage.setItem(STORAGE_KEY, '1');
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* storage blocked: the panel state lives for this page only */ }
  }

  // One state for every card on the page, across every feed root; each toggle mirrors it.
  function setProductsHidden(hidden) {
    var roots = document.querySelectorAll('[data-lookbook-feed]');
    for (var r = 0; r < roots.length; r++) roots[r].classList.toggle('lookbook-feed--products-hidden', hidden);
    var toggles = document.querySelectorAll('[data-lookbook-feed] [data-lookbook-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-expanded', hidden ? 'false' : 'true');
      var label = toggles[i].querySelector('[data-lookbook-toggle-label]');
      if (label) label.textContent = toggles[i].getAttribute(hidden ? 'data-label-show' : 'data-label-hide') || '';
    }
  }

  function restoreHidden() {
    if (readHidden()) setProductsHidden(true);
  }

  // The inline copy of a look's items lives next to the drawer's copy of the
  // same template; suffix the ids the pills are grouped by so a size picked in
  // one never unticks the other.
  function isolateNames(scope) {
    var blocks = scope.querySelectorAll('[data-block-id]');
    for (var b = 0; b < blocks.length; b++) blocks[b].setAttribute('data-block-id', blocks[b].getAttribute('data-block-id') + '-inline');
    var inputs = scope.querySelectorAll('.lookbook-size__input');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var oldId = input.id;
      input.name = input.name + '-inline';
      input.id = oldId + '-inline';
      var label = oldId && scope.querySelector('label[for="' + CSS.escape(oldId) + '"]');
      if (label) label.setAttribute('for', input.id);
    }
  }

  // "split" layout: the featured look's items sit beside the photo on wide
  // screens, cloned from the same template the drawer uses.
  function buildAside(root, item, handle) {
    var card = item.querySelector('[data-lookbook-card]');
    var template = root.querySelector('template[data-lookbook-overlay="' + CSS.escape(handle) + '"]');
    if (!card || !template || card.querySelector('.lookbook-card__aside')) return;
    var aside = document.createElement('div');
    aside.className = 'lookbook-card__aside';
    aside.appendChild(document.importNode(template.content, true));
    var intro = aside.querySelector('.lookbook-drawer__intro');
    if (intro) intro.parentNode.removeChild(intro);
    isolateNames(aside);
    card.appendChild(aside);
    reconcileSet(aside.querySelector('[data-lookbook-set]'));
  }

  // MORE LOOKS: the next N cards in one row (a swipe row on phones), the rest
  // stay out of the landing; ALL LOOKS leads back to the full feed.
  // Inside the More Looks row (itself a horizontal scroller) a card keeps only its
  // hero: the extra slides and the carousel controls leave both layout and the
  // accessibility tree, and the strip is no longer announced as a carousel.
  function flattenCarousel(card) {
    var slides = card.querySelector('[data-lookbook-slides]');
    if (!slides) return;
    var extras = slides.querySelectorAll('[data-lookbook-slide]:not([data-lookbook-slide="1"])');
    for (var i = 0; i < extras.length; i++) extras[i].hidden = true;
    slides.removeAttribute('role');
    slides.removeAttribute('aria-roledescription');
    slides.removeAttribute('aria-label');
    var controls = slides.parentNode.querySelectorAll('[data-lookbook-dots], [data-lookbook-count], [data-lookbook-arrow]');
    for (var c = 0; c < controls.length; c++) controls[c].hidden = true;
  }

  var CARD = '[data-lookbook-item]:not([data-lookbook-archived])';

  function layoutMoreLooks(root, items, featured, more) {
    var count = parseInt(root.getAttribute('data-more-count'), 10) || 4;
    var row = document.createElement('div');
    row.className = 'lookbook-feed__row';
    row.style.setProperty('--lookbook-more-count', String(count));
    if (more.id) {
      row.setAttribute('role', 'region');
      row.setAttribute('aria-labelledby', more.id);
    }
    var others = [];
    var children = items.querySelectorAll(CARD);
    for (var i = 0; i < children.length; i++) if (children[i] !== featured) others.push(children[i]);
    for (var k = 0; k < others.length; k++) {
      if (k < count) { others[k].removeAttribute('data-lookbook-later'); flattenCarousel(others[k]); row.appendChild(others[k]); }
      else others[k].hidden = true;
    }
    if (row.children.length) {
      more.after(row);
      more.hidden = false;
    }
    var all = root.querySelector('[data-lookbook-all]');
    if (all) all.hidden = false;
  }

  function applyFilter(root, tag) {
    var chips = root.querySelectorAll('[data-lookbook-filter]');
    for (var c = 0; c < chips.length; c++) {
      chips[c].setAttribute('aria-pressed', chips[c].getAttribute('data-lookbook-filter') === tag ? 'true' : 'false');
    }
    root.classList.toggle('lookbook-feed--filtered', tag !== '');
    var items = root.querySelectorAll(CARD);
    for (var i = 0; i < items.length; i++) {
      var tags = (items[i].getAttribute('data-tags') || '').split('|');
      items[i].hidden = tag !== '' && tags.indexOf(tag) === -1;
    }
  }

  function loadMore(root) {
    var size = parseInt(root.getAttribute('data-page-size'), 10) || 12;
    var later = root.querySelectorAll('[data-lookbook-item][data-lookbook-later]');
    for (var i = 0; i < later.length && i < size; i++) later[i].removeAttribute('data-lookbook-later');
    var total = root.querySelectorAll(CARD).length;
    var left = root.querySelectorAll('[data-lookbook-item][data-lookbook-later]').length;
    var shown = root.querySelector('[data-lookbook-shown]');
    if (shown) shown.textContent = String(total - left);
    var load = root.querySelector('[data-lookbook-load]');
    if (load && !left) load.hidden = true;
  }

  function promoteLook(root) {
    var params = new URLSearchParams(window.location.search);
    var handle = params.get('look');
    var item = handle && root.querySelector('[data-lookbook-item][data-handle="' + CSS.escape(handle) + '"]');
    if (item) {
      if (item.hasAttribute('data-lookbook-archived')) {
        item.removeAttribute('data-lookbook-archived');
        item.hidden = false;
        // every look may be archived: the empty state is then on the page too
        var empty = root.querySelectorAll('[data-lookbook-empty]');
        for (var e = 0; e < empty.length; e++) empty[e].hidden = true;
      }
      var items = root.querySelector('[data-lookbook-items]');
      var more = root.querySelector('[data-lookbook-more]');
      var card = item.querySelector('[data-lookbook-card]');
      var layout = root.getAttribute('data-featured-layout') || 'overlay';
      var featuredStyle = root.getAttribute('data-panel-style-featured');
      root.classList.add('lookbook-feed--landing');
      item.classList.add('is-featured');
      item.removeAttribute('data-lookbook-later');
      if (layout === 'split') {
        item.classList.add('is-split');
        buildAside(root, item, handle);
      } else if (card && featuredStyle) {
        card.className = card.className.replace(/lookbook-card--panel-\S+/, 'lookbook-card--panel-' + featuredStyle);
      }
      items.insertBefore(item, items.firstChild);
      if (more) {
        item.after(more);
        layoutMoreLooks(root, items, item, more);
      }
      var current = root.querySelector('[data-lookbook-crumb-current]');
      var all = root.querySelector('[data-lookbook-crumb-all]');
      if (current && all) {
        current.hidden = true;
        all.hidden = false;
      }
    }
    root.classList.remove('lookbook-feed--pending');
  }

  // The tag chips stick under the header; the header hides and returns on
  // scroll, so the offset follows its visible bottom edge.
  function trackStickyTop(root) {
    if (!root.querySelector('[data-lookbook-chips]')) return;
    var header = document.querySelector('.shopify-section-header');
    var queued = false;
    var settle;
    function update() {
      queued = false;
      var bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      root.style.setProperty('--lookbook-sticky-top', Math.round(bottom) + 'px');
    }
    // The header slides in after the last scroll event, so measure again once
    // its transition has had time to finish.
    function queue() {
      clearTimeout(settle);
      settle = setTimeout(update, 400);
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    if (header) header.addEventListener('transitionend', update);
    update();
  }

  // A link can open the feed already filtered: ?tag=SALE presses that chip.
  // A tag with no chip is ignored and the feed opens unfiltered. A story
  // landing (?look=) has already laid the page out and hides the chips, so
  // the tag is ignored there too.
  function filterFromUrl(root) {
    if (root.classList.contains('lookbook-feed--landing')) return;
    var tag = new URLSearchParams(window.location.search).get('tag');
    if (!tag) return;
    var chip = root.querySelector('[data-lookbook-filter]');
    if (!chip) return;
    var chips = root.querySelectorAll('[data-lookbook-filter]');
    for (var c = 0; c < chips.length; c++) {
      if (chips[c].getAttribute('data-lookbook-filter') === tag) {
        applyFilter(root, tag);
        return;
      }
    }
  }

  function initFeed(root) {
    if (!root || root.hasAttribute('data-lookbook-ready')) return;
    root.setAttribute('data-lookbook-ready', '');
    restoreHidden();
    promoteLook(root);
    trackStickyTop(root);
    filterFromUrl(root);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target.closest) return;

    var opener = target.closest('[data-lookbook-open]');
    if (opener) {
      var root = feedFor(opener);
      var drawer = root && root.querySelector('lookbook-drawer');
      var item = opener.closest('[data-lookbook-item]');
      if (!drawer || !item) return;
      event.preventDefault();
      drawer.open(opener.getAttribute('data-lookbook-open'), {
        trigger: opener,
        name: item.getAttribute('data-name') || '',
        productId: opener.getAttribute('data-lookbook-product') || ''
      });
      return;
    }

    var toggle = target.closest('[data-lookbook-toggle]');
    if (toggle) {
      var hidden = !feedFor(toggle).classList.contains('lookbook-feed--products-hidden');
      setProductsHidden(hidden);
      writeHidden(hidden);
      return;
    }

    var chip = target.closest('[data-lookbook-filter]');
    if (chip) {
      applyFilter(feedFor(chip), chip.getAttribute('data-lookbook-filter') || '');
      return;
    }

    var loader = target.closest('[data-lookbook-load-more]');
    if (loader) loadMore(feedFor(loader));
  });

  // An unresolved per-item add must not reach the base theme's AddToCart handler
  // (it would silently do nothing); it asks for a size instead.
  document.addEventListener('click', function (event) {
    var add = event.target.closest && event.target.closest('[data-lookbook-add][aria-disabled="true"]');
    if (!add) return;
    event.preventDefault();
    event.stopPropagation();
    showHint(add.closest('bundle-product'));
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var add = event.target.closest && event.target.closest('[data-lookbook-add]');
    if (!add) return;
    event.preventDefault();
    add.click();
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.classList || !input.classList.contains('lookbook-size__input')) return;
    var set = input.closest('[data-lookbook-set]');
    var marked = input.closest('.lookbook-drawer__item--needs-size');
    if (marked) marked.classList.remove('lookbook-drawer__item--needs-size');
    if (set) reconcileSet(set);
  });

  // lookbook-variants.js swaps the color in place on the same click and picks a
  // default size; the set rule is re-applied once that handler has run.
  document.addEventListener('click', function (event) {
    var swatch = event.target.closest && event.target.closest('[data-lb-swatch]');
    if (!swatch) return;
    var set = swatch.closest('[data-lookbook-set]');
    if (set) setTimeout(function () { reconcileSet(set); }, 0);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-lookbook-submit]');
    if (!button || button.disabled) return;
    var set = button.closest('[data-lookbook-set]');
    if (!set) return;
    reconcileSet(set);
    if (button.getAttribute('aria-disabled') !== 'true') return;
    // sizes still missing: the base theme's bundle-products must not run (it
    // would add the resolved items only); show the first item that needs a size.
    event.preventDefault();
    event.stopPropagation();
    var rows = set.querySelectorAll('bundle-product');
    for (var i = 0; i < rows.length; i++) {
      if (reconcileRow(rows[i]).state !== 'unresolved') continue;
      flagMissing(set, rows[i], button);
      return;
    }
  }, true);

  // Tag the cart with the look an add starts from. When a cart already exists
  // (cart cookie) the tag goes out on the click itself, in parallel with the
  // base theme's own add request; keepalive lets it finish while the page
  // navigates on the postLink path (cart drawer off). Without a cart, two
  // parallel requests would each create their own cart and the tag would land
  // on the one the browser drops, so: on the AJAX path the tag waits for
  // ajaxProduct:added; on the navigation path the click is held until the tag
  // has created the cart, then replayed. A tag on an add that later fails is
  // harmless: reporting matches the look's products against the order lines.
  function hasCart() {
    return /(^|;\s*)cart=/.test(document.cookie);
  }

  // mirrors the base theme's own branch in AddToCart / BundleProducts
  function usesAjaxCart() {
    return !document.body.classList.contains('template-cart')
      && !!(window.theme && theme.shopSettings && theme.shopSettings.cartDrawer);
  }

  function tagCart(handle, options) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var init = {
      method: 'POST',
      keepalive: !(options && options.signal),
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { lookbook: handle } })
    };
    if (options && options.signal) init.signal = options.signal;
    try {
      return fetch(root + 'cart/update.js', init).catch(function () { /* attribution only */ });
    } catch (_) {
      return Promise.resolve();
    }
  }

  // The held click waits for the tag request to settle. A slow request is
  // aborted after the timeout, which settles it too, so the theme's own add
  // never runs in parallel with a request that could still create a cart.
  function tagCartHeld(handle, timeoutMs) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, timeoutMs) : null;
    return tagCart(handle, controller ? { signal: controller.signal } : null).then(function () {
      if (timer) clearTimeout(timer);
    });
  }

  var pendingTag = null;
  document.addEventListener('click', function (event) {
    var control = event.target.closest && event.target.closest('[data-lookbook-submit], [data-lookbook-add]');
    if (!control || control.getAttribute('aria-disabled') === 'true' || control.disabled) return;
    if (control.hasAttribute('data-lookbook-tagged')) {
      control.removeAttribute('data-lookbook-tagged');
      return;
    }
    var set = control.closest('[data-lookbook-set]');
    var handle = set && set.getAttribute('data-lookbook-handle');
    if (!handle) return;
    if (hasCart()) {
      tagCart(handle);
      pendingTag = null;
      return;
    }
    if (usesAjaxCart()) {
      pendingTag = { handle: handle, at: Date.now() };
      return;
    }
    // no cart yet and the theme is about to navigate: create the cart with the
    // tag first, then replay the click so the theme's own handler runs
    event.preventDefault();
    event.stopImmediatePropagation();
    var replayed = false;
    var replay = function () {
      if (replayed) return;
      replayed = true;
      control.setAttribute('data-lookbook-tagged', '');
      control.click();
    };
    tagCartHeld(handle, 2500).then(replay, replay);
  }, true);

  // The cart drawer takes over once something was added; the look drawer closes,
  // and a tag that waited for the cart to exist goes out now.
  document.addEventListener('ajaxProduct:added', function () {
    var drawers = document.querySelectorAll('lookbook-drawer');
    for (var i = 0; i < drawers.length; i++) {
      if (drawers[i].isOpen()) drawers[i].closeMenuDrawer(new CustomEvent('lookbook:close'), null);
    }
    if (pendingTag && Date.now() - pendingTag.at < 15000) tagCart(pendingTag.handle);
    pendingTag = null;
  });

  // Several photos per look: the slides scroll natively (scroll-snap); this only
  // keeps the dots in step and drives the wide-screen arrows. Delegated, so a
  // card that the story landing moves to the top keeps working.
  function slideIndex(slides) {
    // RTL scrollers report 0 on the first slide and negative offsets after it
    return slides.clientWidth ? Math.round(Math.abs(slides.scrollLeft) / slides.clientWidth) : 0;
  }
  function syncDots(slides) {
    var media = slides.parentNode;
    var dots = media && media.querySelector('[data-lookbook-dots]');
    if (!dots || !slides.clientWidth) return;
    var index = slideIndex(slides);
    var list = dots.children;
    for (var i = 0; i < list.length; i++) list[i].classList.toggle('is-active', i === index);
    var current = media.querySelector('[data-lookbook-count-current]');
    var label = String(index + 1);
    if (current && current.textContent !== label) current.textContent = label;
    // no wrap-around, like Instagram: the arrow at an end disappears
    var prev = media.querySelector('[data-lookbook-arrow="-1"]');
    var next = media.querySelector('[data-lookbook-arrow="1"]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= list.length - 1;
  }
  document.addEventListener('scroll', function (event) {
    var slides = event.target;
    if (slides && slides.nodeType === 1 && slides.hasAttribute && slides.hasAttribute('data-lookbook-slides')) syncDots(slides);
  }, true);
  document.addEventListener('click', function (event) {
    var arrow = event.target.closest && event.target.closest('[data-lookbook-arrow]');
    if (!arrow) return;
    var slides = arrow.parentNode.querySelector('[data-lookbook-slides]');
    if (!slides) return;
    event.preventDefault();
    var dir = parseInt(arrow.getAttribute('data-lookbook-arrow'), 10) || 1;
    var items = slides.children;
    if (!items.length) return;
    var next = slideIndex(slides) + dir;
    if (next < 0 || next >= items.length) return;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // scroll the strip itself, never the page; in RTL the strip's offsets run negative
    var rtl = getComputedStyle(slides).direction === 'rtl';
    slides.scrollBy({ left: (rtl ? -dir : dir) * slides.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
  });

  var feeds = document.querySelectorAll('[data-lookbook-feed]');
  for (var f = 0; f < feeds.length; f++) initFeed(feeds[f]);

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector && event.target.querySelector('[data-lookbook-feed]');
    if (root) initFeed(root);
  });
})();
